import 'photoswipe/style.css';

import PhotoSwipeLightbox from 'photoswipe/lightbox';
import { getThumbnailUrl } from '~~/utils/videos';
import getVideoId from 'get-video-id';
import {
    type SimpleProductDataFragment,
} from '~~/graphql';

type Media = SimpleProductDataFragment['medias'][0];

function getVideoPlayerUrl(url: string) {
    const { service, id } = getVideoId(url);
    if (service === 'vimeo' && id) {
        return `https://player.vimeo.com/video/${id}`;
    }
    return url;
}

type LightBoxParameters = ConstructorParameters<typeof PhotoSwipeLightbox>[0];

export function useProductGallery({
    lightBoxParameters,
    medias,
}: {
    lightBoxParameters: MaybeRef<LightBoxParameters>
    medias: MaybeRef<Media[]>
}) {
    const img = useImage();

    const lightBox = ref<PhotoSwipeLightbox>();
    const thumbnailsByMedia = ref<Record<string, string>>({});

    function parseVideo() {
        if (!lightBox.value) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        lightBox.value.addFilter('itemData', (itemData: any) => {
            const { videoUrl } = itemData.element.dataset;
            if (videoUrl) {
                itemData.videoUrl = videoUrl;
            }
            return itemData;
        });
    }

    function runLightBox() {
        lightBox.value = new PhotoSwipeLightbox(unref(lightBoxParameters));

        lightBox.value.on('contentLoad', (event) => {
            const { content } = event;

            if (content.type === 'model') {
                // prevent the default behavior
                event.preventDefault();

                // Create a container for iframe
                // and assign it to the `content.element` property
                content.element = document.createElement('div');
                content.element.className = 'pswp__iframe-container';

                const iframe = document.createElement('iframe');

                iframe.setAttribute('allowfullscreen', '');
                iframe.src = content.data.src!;

                content.element.append(iframe);
            } else if (content.type === 'video') {
                // prevent the deafult behavior
                event.preventDefault();

                // Create a container for iframe
                // and assign it to the `content.element` property
                content.element = document.createElement('div');
                content.element.className = 'pswp__iframe-container';

                const iframe = document.createElement('iframe');
                iframe.setAttribute('allowfullscreen', '');
                iframe.src = getVideoPlayerUrl(content.data.videoUrl);
                content.element.append(iframe);
            }
        });

        lightBox.value.init();
    }

    const populateVideoThumbnails = () => {
        for (const media of unref(medias)) {
            if (media.__typename === 'ProductVideo' && media.url) {
                const { service, id } = getVideoId(media.url);
                if (service && id) {
                    // Purposefully not using async, thumbnails may load in any order.
                    getThumbnailUrl(service, id).then((url) => {
                        thumbnailsByMedia.value[media.id] = url;
                    }).catch((error) => {
                        // eslint-disable-next-line no-console
                        console.warn('Failed to fetch video thumbnail:');
                        // eslint-disable-next-line no-console
                        console.warn(error);
                    });
                } else {
                    // eslint-disable-next-line no-console
                    console.warn(`Invalid video URL: ${media.url}`);
                }
            }
        }
    };

    function getImageUrl(url: string) {
        return img(url, {
            width: 2000,
            height: 2000,
        });
    }

    function getImageSourceSet(url: string) {
        return img.getSizes(url, {
            sizes: 'xs:90vw sm:90vw md:90vw lg:90vw xl:90vw 2xl:90vw',
        }).srcset;
    }

    onMounted(() => {
        runLightBox();
        parseVideo();
        populateVideoThumbnails();
    });

    onUnmounted(() => {
        lightBox.value?.destroy();
    });

    return {
        lightBox,
        thumbnailsByMedia,
        getVideoPlayerUrl,
        getImageUrl,
        getImageSourceSet,
    };
}
